import { Dropdown } from 'antd';
import { Link } from 'react-router-dom';

import { env } from '@/config/env';
import { useAppSelector } from '@/hooks/useAppSelector';

import { useGetMyMenuTreeQuery } from '../api/menusApi';
import type { MenuTreeNode } from '../types/menu.types';
import { menuIconFromString } from '../utils/menuIconMap';
import { filterMapShellHeaderMenuTree } from '../utils/menuTreeFilters';

const linkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.3px',
  textDecoration: 'none',
  color: 'var(--text-secondary)',
  border: '1px solid transparent',
  background: 'transparent',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function LeafLink({ node }: { node: MenuTreeNode }) {
  const path = typeof node.path === 'string' ? node.path.trim() : '';
  if (!path) return null;
  if (path.startsWith('http')) {
    return (
      <a href={path} style={{ ...linkStyle, borderColor: 'var(--glass-border)' }} rel="noreferrer">
        {menuIconFromString(node.icon)}
        {node.label}
      </a>
    );
  }
  return (
    <Link to={path} style={{ ...linkStyle, borderColor: 'var(--glass-border)' }}>
      {menuIconFromString(node.icon)}
      {node.label}
    </Link>
  );
}

function MenuEntry({ node }: { node: MenuTreeNode }) {
  const children = [...(node.children ?? [])]
    .filter((c) => c.is_active)
    .sort((a, b) => a.sort_order - b.sort_order);

  if (children.length > 0) {
    return (
      <Dropdown
        menu={{
          items: children
            .map((c) => {
              const p = typeof c.path === 'string' ? c.path.trim() : '';
              if (!p) return null;
              return {
                key: c.id,
                label: p.startsWith('http') ? (
                  <a href={p} rel="noreferrer">
                    {c.label}
                  </a>
                ) : (
                  <Link to={p}>{c.label}</Link>
                ),
              };
            })
            .filter((x): x is NonNullable<typeof x> => x != null),
        }}
        trigger={['click']}
      >
        <button type="button" style={{ ...linkStyle, borderColor: 'var(--glass-border)' }}>
          {menuIconFromString(node.icon)}
          {node.label}
        </button>
      </Dropdown>
    );
  }

  return <LeafLink node={node} />;
}

/**
 * Menu from `GET /api/menus/me`, filtered with `filterMapShellHeaderMenuTree`
 * (no ops/admin, no `/dashboard` or `/map/iot` shortcuts on the map header).
 */
export function DashboardShellMenuLinks() {
  const user = useAppSelector((s) => s.auth.user);
  const skip = !user || !env.API_BASE_URL.trim();
  const { data: tree, isError } = useGetMyMenuTreeQuery(undefined, {
    skip,
  });

  if (skip || isError) return null;
  if (!tree?.length) return null;

  const roots = filterMapShellHeaderMenuTree(tree);

  if (!roots.length) return null;

  return (
    <nav
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        maxWidth: 'min(720px, 58vw)',
        overflowX: 'auto',
      }}
      aria-label="App menu"
    >
      {roots.map((node) => (
        <MenuEntry key={node.id} node={node} />
      ))}
    </nav>
  );
}
